import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { AssetTransfer } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
import { formatDate } from '../../utils/dateUtils';

interface Props {
  transfers: AssetTransfer[];
  /** The caller may reverse (ASSET_TRANSFER_MANAGE); a reversal that posts also needs FIXED_ASSET_MANAGE server-side. */
  canReverse: boolean;
  /** A transfer that moved the segment (with or without a journal) also needs FIXED_ASSET_MANAGE to reverse. */
  canReverseReclass: boolean;
  onReverse: (t: AssetTransfer) => void;
}

const change = (from?: string | null, to?: string | null, fromId?: string | null, toId?: string | null) =>
  (fromId ?? null) === (toId ?? null) ? null : `${from ?? '—'} → ${to ?? '—'}`;

/** F10: the asset's transfer history, newest first. Only the latest posted transfer can be reversed. */
/** The transfer moved the asset between segments (a reclass), whether or not it carried a journal. */
export const isSegmentMove = (t: AssetTransfer): boolean =>
  !!t.journalEntryId || (t.fromSegmentId ?? null) !== (t.toSegmentId ?? null);

const AssetTransferHistory: React.FC<Props> = ({ transfers, canReverse, canReverseReclass, onReverse }) => (
  <Paper sx={{ p: 3, mb: 2 }} data-testid="asset-transfer-history">
    <Typography variant="subtitle1" sx={{ mb: 1 }}>Transfers</Typography>
    {transfers.length === 0 ? (
      <Typography variant="body2" color="text.secondary">No transfers yet.</Typography>
    ) : (
      <TableContainer>
        <Table size="small" data-help-section="fixedAssets.xfer.table">
          <TableHead>
            <TableRow>
              <TableCell data-help="fixedAssets.xfer.col.no">No.</TableCell>
              <TableCell data-help="fixedAssets.xfer.col.date">Date</TableCell>
              <TableCell data-help="fixedAssets.xfer.col.change">Change</TableCell>
              <TableCell data-help="fixedAssets.xfer.col.reason">Reason</TableCell>
              <TableCell data-help="fixedAssets.xfer.col.journal">Journal</TableCell>
              <TableCell data-help="fixedAssets.xfer.col.status">Status</TableCell>
              <TableCell data-help="fixedAssets.xfer.col.actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {transfers.map(t => {
              const lines = [
                ['Location', change(t.fromLocationName, t.toLocationName, t.fromLocationId, t.toLocationId)],
                ['Department', change(t.fromDepartmentName, t.toDepartmentName, t.fromDepartmentId, t.toDepartmentId)],
                ['Custodian', change(t.fromCustodianName, t.toCustodianName, t.fromCustodianUserId, t.toCustodianUserId)],
                ['Segment', change(t.fromSegmentName ?? 'Common', t.toSegmentName ?? 'Common', t.fromSegmentId, t.toSegmentId)],
              ].filter(([, v]) => v);
              return (
                <TableRow key={t.id}>
                  <TableCell>{t.transferNo}</TableCell>
                  <TableCell>{formatDate(t.transferDate)}</TableCell>
                  <TableCell>
                    {lines.map(([k, v]) => <Box key={k as string}><b>{k}:</b> {v}</Box>)}
                  </TableCell>
                  <TableCell>{t.reason ?? '—'}</TableCell>
                  <TableCell>
                    {t.journalEntryId
                      ? <Link component={RouterLink} to={`/accounting/journal-entries/${t.journalEntryId}`}>Reclass JE</Link>
                      : '—'}
                    {t.reversalJournalEntryId && (
                      <>{' · '}<Link component={RouterLink} to={`/accounting/journal-entries/${t.reversalJournalEntryId}`}>Reversal JE</Link></>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={t.status === 'POSTED' ? 'Posted' : 'Reversed'}
                      color={t.status === 'POSTED' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    {canReverse && t.reversible && (canReverseReclass || !isSegmentMove(t)) && (
                      <Button data-help="fixedAssets.xfer.action.reverse" size="small" color="warning" onClick={() => onReverse(t)}>Reverse</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Paper>
);

export default AssetTransferHistory;
